import type { InterpretationSnapshotPayload } from '../redux';

export type SemanticDiffKind = 'added' | 'removed' | 'changed';

export type SemanticDiffEntry = {
  path: string;
  kind: SemanticDiffKind;
  before?: unknown;
  after?: unknown;
};

const IGNORED_KEYS = new Set([
  'created',
  'created_by',
  'changed',
  'changed_by',
  'id',
  'interpreted',
  'interpreted_by',
  'last_edited',
  'rendered_html',
  'selection_type_creation_date',
  'updated',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalize = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normalize);
  }

  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !IGNORED_KEYS.has(key))
        .map(([key, nestedValue]) => [key, normalize(nestedValue)]),
    );
  }

  return value;
};

const collectDiffs = (
  before: unknown,
  after: unknown,
  path: string,
  diffs: SemanticDiffEntry[],
) => {
  if (Object.is(before, after)) {
    return;
  }

  if (Array.isArray(before) && Array.isArray(after)) {
    const itemCount = Math.max(before.length, after.length);

    for (let index = 0; index < itemCount; index += 1) {
      const itemPath = `${path}[${index}]`;

      if (index >= before.length) {
        diffs.push({ path: itemPath, kind: 'added', after: after[index] });
      } else if (index >= after.length) {
        diffs.push({ path: itemPath, kind: 'removed', before: before[index] });
      } else {
        collectDiffs(before[index], after[index], itemPath, diffs);
      }
    }

    return;
  }

  if (isRecord(before) && isRecord(after)) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);

    for (const key of Array.from(keys).sort()) {
      const keyPath = `${path}.${key}`;

      if (!(key in before)) {
        diffs.push({ path: keyPath, kind: 'added', after: after[key] });
      } else if (!(key in after)) {
        diffs.push({ path: keyPath, kind: 'removed', before: before[key] });
      } else {
        collectDiffs(before[key], after[key], keyPath, diffs);
      }
    }

    return;
  }

  diffs.push({ path, kind: 'changed', before, after });
};

export const compareInterpretationSnapshots = (
  snapshotPayload: InterpretationSnapshotPayload,
  currentPayload: InterpretationSnapshotPayload,
): SemanticDiffEntry[] => {
  const diffs: SemanticDiffEntry[] = [];

  collectDiffs(
    normalize(snapshotPayload),
    normalize(currentPayload),
    '$',
    diffs,
  );

  return diffs;
};
